import { useNavigate } from 'react-router-dom'
import { Box, Button, HStack, VStack, Text, Badge, Spinner, Alert } from '@chakra-ui/react';
import { FiPlus, FiEdit2 } from 'react-icons/fi'
import { CanvassingPageShell } from '../components/canvassing/CanvassingPageShell'
import { useScripts } from '../hooks/useScript'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared'
import type { ScriptSummaryDto } from '../types/script'

export function CanvassingScriptsPage() {
  const navigate = useNavigate()
  const { data: scripts, isLoading, error } = useScripts()

  const columns: ResponsiveColumn<ScriptSummaryDto>[] = [
    {
      header: 'Label',
      mobileRole: 'title',
      cell: (script) => <Text fontWeight="medium">{script.label}</Text>,
    },
    {
      header: 'Description',
      mobileRole: 'meta',
      cell: (script) => (
        <Text fontSize="sm" lineClamp={2}>
          {script.description || '—'}
        </Text>
      ),
    },
    {
      header: 'Elements',
      mobileRole: 'meta',
      cell: (script) => <Text fontSize="sm">{script.elementCount}</Text>,
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (script) => (
        <Badge colorPalette={script.isActive ? 'green' : 'gray'} fontSize="xs">
          {script.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      header: 'Created',
      mobileRole: 'meta',
      cell: (script) => (
        <VStack align="start" gap={0}>
          <Text fontSize="sm">{new Date(script.createdAt).toLocaleDateString()}</Text>
          <Text fontSize="xs" color="gray.500">
            {script.createdByName}
          </Text>
        </VStack>
      ),
    },
  ]

  return (
    <CanvassingPageShell
      title="Scripts"
      subtitle="Create and manage scripts for canvassing sessions"
    >
      <VStack gap={6} align="stretch">
        <HStack justify="flex-end">
          <Button
            colorPalette="brand"
            size="sm"
            onClick={() => navigate('/dashboard/canvassing/scripts/new')}><FiPlus />Create Script
                      </Button>
        </HStack>

        {isLoading && (
          <VStack py={12}>
            <Spinner size="lg" color="accent" />
            <Text color="gray.500">Loading scripts...</Text>
          </VStack>
        )}

        {!!error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            Failed to load scripts. Please try again.
          </Alert.Root>
        )}

        {!isLoading && scripts && scripts.length === 0 && (
          <Box bg="surface" p={12} borderRadius="lg" shadow="md" textAlign="center">
            <Text color="gray.500" mb={4}>No scripts found</Text>
            <Button
              colorPalette="brand"
              size="sm"
              onClick={() => navigate('/dashboard/canvassing/scripts/new')}><FiPlus />Create Your First Script
                          </Button>
          </Box>
        )}

        {!isLoading && scripts && scripts.length > 0 && (
          <ResponsiveTable
            columns={columns}
            rows={scripts}
            keyFn={(script) => script.id}
            rowActions={[
              {
                label: 'Edit',
                icon: FiEdit2,
                onClick: (script) =>
                  navigate(`/dashboard/canvassing/scripts/${script.id}/edit`),
              },
            ]}
          />
        )}
      </VStack>
    </CanvassingPageShell>
  );
}
